import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { ArrowLeft, Boxes, Trash2 } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError, getErrorCodeForUi } from '@/api/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { Sidebar } from '@/components/shell/Sidebar';
import { useSetBreadcrumbs } from '@/components/shell/ShellContext';
import { usePermissions } from '@/auth/usePermissions';
import { toast } from '@/hooks/useToast';

import {
  readCoreJobId,
  readLaunchJobId,
  useApplyTenantTemplate,
  useDmAvailable,
  useJob,
  useTenant,
  useTenantTemplates,
  useWipeTenant,
} from '../api';
import { ApplyTemplateDialog } from './ApplyTemplateDialog';
import { JobProgressIndicator } from './JobProgressIndicator';
import { OverridesPanel } from './OverridesPanel';
import { TenantFederatedIdentitiesPanel } from './TenantFederatedIdentitiesPanel';
import { TenantRolesPanel } from './TenantRolesPanel';
import { TenantUsersPanel } from './TenantUsersPanel';
import { WipeTenantConfirmDialog } from './WipeTenantConfirmDialog';
import { TENANT_DEFAULT_SECTION_ID, tenantSidebarGroups } from './tenantSidebar';

/**
 * Tenant record page (PTV2-1383). Restyled from the old full-bleed tab strip
 * into a contextual-sidebar record, mirroring the patient record: opening a
 * tenant swaps the left rail to a tenant-scoped table of contents and the
 * body scrolls independently of the shell. The active section is driven by
 * the URL hash (`…/tenants/{uuid}#users`) exactly like `PatientRecordView`.
 *
 * The wipe/apply-template job state stays owned here (not in the section
 * bodies) so it survives section switches while a job is in flight.
 */
export function TenantDetailView(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { uuid = null } = useParams<{ uuid: string }>();
  const { canManagePlatform } = usePermissions();

  const tenantQuery = useTenant(uuid);
  const { available: dmAvailable } = useDmAvailable();
  const templatesQuery = useTenantTemplates();
  const applyTemplate = useApplyTenantTemplate();
  const wipeTenant = useWipeTenant();

  const [applyOpen, setApplyOpen] = useState(false);
  const [applyJobId, setApplyJobId] = useState<number | null>(null);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeJobId, setWipeJobId] = useState<number | null>(null);
  const navigatedAwayRef = useRef(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const tenant = tenantQuery.data ?? null;

  // PTV2-1383: publish the trail to the APPLICATION top bar (like every other page) rather than a
  // back-link in this view's own content header — "Administration › Tenants › <tenant>".
  useSetBreadcrumbs([
    { label: t('shell.topNav.admin'), to: '/platform' },
    { label: t('routes.platform.sidebar.tenants'), to: '/platform/tenants' },
    ...(tenant === null ? [] : [{ label: tenant.label.length > 0 ? tenant.label : tenant.name }]),
  ]);
  const templates = templatesQuery.data ?? [];

  // Watch the wipe job. ONLY a COMPLETED wipe means the schema is gone — then we
  // leave the (now-gone) detail view and refresh the list. A FAILED/STOPPED/
  // ABANDONED wipe must NOT navigate away silently; we keep the operator here
  // and surface the failure (see the wipe-job indicator below). (CR1)
  const wipeJob = useJob(wipeJobId);
  const wipeSucceeded = wipeJob.data?.status === 'COMPLETED';
  const wipeFailed =
    wipeJob.data?.isTerminal === true && wipeJob.data.status !== 'COMPLETED';

  // The detail tenant query polls while the tenant is wiping; once the wipe
  // completes the tenant 404s. Treat a 404 on a tenant we were wiping as
  // "wipe completed" so we navigate back instead of 404-looping. (CR3)
  const wipedOut =
    wipeJobId !== null &&
    tenantQuery.isError &&
    tenantQuery.error instanceof ApiError &&
    tenantQuery.error.status === 404;

  useEffect(() => {
    if ((wipeSucceeded || wipedOut) && !navigatedAwayRef.current) {
      navigatedAwayRef.current = true;
      void navigate('/platform/tenants');
    }
  }, [wipeSucceeded, wipedOut, navigate]);

  // The Image-overrides section shows when the tenant carries overrides OR when
  // DM is reachable (so an operator can add the first override). Hidden entirely
  // in production (DM unreachable) for plain tenants.
  const showOverrides = tenant !== null && (tenant.hasOverrides || dmAvailable);
  const showDangerZone = dmAvailable && canManagePlatform;

  // Active section from the URL hash. Falls back to the default when there's no
  // hash, or when the hash points at the (hidden) overrides section.
  const hashId = hash.length > 1 ? hash.slice(1) : TENANT_DEFAULT_SECTION_ID;
  const activeId =
    hashId === 'overrides' && !showOverrides ? TENANT_DEFAULT_SECTION_ID : hashId;

  // Landing on a new section starts at the top of the body, rather than
  // carrying over the previous section's scroll position (mirrors the patient
  // record, PTV2-1206).
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0 });
  }, [activeId]);

  function handleApply(templateName: string): void {
    if (uuid === null) return;
    applyTemplate.mutate(
      { tenantUuid: uuid, templateName },
      {
        onSuccess: (launch) => {
          setApplyOpen(false);
          applyTemplate.reset();
          // `jobId` is a string on the wire; GET /jobs/{id} expects a Long.
          // Fail loudly if the launch carried no usable id rather than tracking
          // a phantom job. (CR6)
          const jobId = readCoreJobId(launch);
          if (jobId === null) {
            toast({
              title: t('routes.platform.detail.applyTemplate.noJob'),
              variant: 'destructive',
            });
            return;
          }
          setApplyJobId(jobId);
          toast({
            title: t('routes.platform.detail.applyTemplate.launched', { id: jobId }),
            variant: 'success',
          });
        },
        onError: (error) => {
          toast({
            title: t('routes.platform.detail.applyTemplate.error', {
              code: getErrorCodeForUi(error),
            }),
            variant: 'destructive',
          });
        },
      },
    );
  }

  function handleWipe(target: { uuid: string }): void {
    wipeTenant.mutate(target.uuid, {
      onSuccess: (launch) => {
        setWipeOpen(false);
        wipeTenant.reset();
        // `DeploymentActionResponse` is `@JsonInclude(NON_NULL)`, so the
        // backend can omit `jobExecutionId`. Fail loudly instead of tracking a
        // never-terminal job (and never silently navigating away). (CR6)
        const jobId = readLaunchJobId(launch);
        if (jobId === null) {
          toast({ title: t('routes.platform.detail.wipe.noJob'), variant: 'destructive' });
          return;
        }
        setWipeJobId(jobId);
        toast({
          title: t('routes.platform.detail.wipe.launched', { id: jobId }),
          variant: 'success',
        });
      },
      onError: (error) => {
        toast({
          title: t('routes.platform.detail.wipe.error', { code: getErrorCodeForUi(error) }),
          variant: 'destructive',
        });
      },
    });
  }

  // Pending / error states render a plain full-bleed frame (no contextual rail —
  // there's no tenant to scope it to yet).
  if (tenant === null) {
    return (
      <div className="flex h-full flex-col">
        <DetailHeaderFrame>
          {tenantQuery.isPending && <Skeleton className="h-7 w-64" />}
          {tenantQuery.isError &&
            (tenantQuery.error instanceof ApiError && tenantQuery.error.status === 404 ? (
              <p role="alert" className="text-sm text-red-600">
                {t('routes.platform.detail.notFound')}
              </p>
            ) : (
              <div className="max-w-xl">
                <FetchErrorCallout
                  error={tenantQuery.error}
                  heading={t('routes.platform.detail.error.heading')}
                  message={t('routes.platform.detail.error.message')}
                  codeLabel={t('routes.platform.detail.error.codeLabel')}
                  onRetry={() => {
                    tenantQuery.refetch().catch(() => undefined);
                  }}
                  retryLabel={t('routes.platform.detail.error.retry')}
                />
              </div>
            ))}
        </DetailHeaderFrame>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <Sidebar groups={tenantSidebarGroups(tenant.uuid, t, { showOverrides })} />
      <main
        aria-label={tenant.label.length > 0 ? tenant.label : tenant.name}
        className="flex-1 flex flex-col min-w-0 min-h-0 overflow-clip"
      >
        {/* Identity band — the tenant analogue of PatientIdentityBand. Navigation back to the list is
            the "Tenants" crumb in the application top bar (see useSetBreadcrumbs above). */}
        <header className="border-b border-border bg-card px-8 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
                <Boxes className="h-[18px] w-[18px] text-muted-foreground" aria-hidden="true" />
                <span className="truncate">{tenant.label.length > 0 ? tenant.label : tenant.name}</span>
                <Badge variant={tenant.state === 'active' ? 'secondary' : 'outline'}>
                  {t(`routes.platform.tenants.state.${tenant.state}`)}
                </Badge>
              </h1>
              <p className="mt-1 text-[12px] text-muted-foreground">
                {t('routes.platform.detail.schemaLabel')}:{' '}
                <code className="font-mono text-foreground">{tenant.schemaName}</code> ·{' '}
                {t('routes.platform.detail.uuidLabel')}:{' '}
                <code className="font-mono">{tenant.uuid}</code>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setApplyOpen(true); }}
                disabled={templates.length === 0}
              >
                {t('routes.platform.detail.applyTemplate.action')}
              </Button>
            </div>
          </div>
        </header>

        {/* Scroll region — only this scrolls; the shell/rail/band stay put. */}
        <div
          ref={scrollerRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-background"
        >
          <div className="mx-auto max-w-[1100px] px-8 py-6">
            {activeId === 'overview' && (
              <TenantOverviewSection
                schemaName={tenant.schemaName}
                uuid={tenant.uuid}
                name={tenant.name}
                stateLabel={t(`routes.platform.tenants.state.${tenant.state}`)}
                hasOverrides={tenant.hasOverrides}
              />
            )}

            {activeId === 'settings' && (
              <section>
                <h2 className="text-sm font-semibold">
                  {t('routes.platform.detail.settings.heading')}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('routes.platform.detail.settings.placeholder')}
                </p>
              </section>
            )}

            {activeId === 'users' && uuid !== null && <TenantUsersPanel tenantUuid={uuid} />}

            {activeId === 'roles' && uuid !== null && <TenantRolesPanel tenantUuid={uuid} />}

            {activeId === 'federated-identities' && uuid !== null && (
              <TenantFederatedIdentitiesPanel tenantUuid={uuid} />
            )}

            {activeId === 'overrides' && showOverrides && (
              <>
                <OverridesPanel tenant={tenant} canManage={canManagePlatform} />
                {showDangerZone && (
                  <div className="mt-8 rounded-md border border-destructive/40 bg-destructive/5 p-4">
                    <h3 className="text-sm font-semibold text-destructive">
                      {t('routes.platform.detail.danger.heading')}
                    </h3>
                    <div className="mt-3">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        disabled={wipeJobId !== null && !wipeFailed}
                        onClick={() => { setWipeOpen(true); }}
                      >
                        <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
                        {t('routes.platform.detail.danger.wipe')}
                      </Button>
                    </div>
                    {wipeJobId !== null && (
                      <div className="mt-3">
                        <JobProgressIndicator jobExecutionId={wipeJobId} />
                        {wipeFailed && (
                          <p role="alert" className="mt-2 text-sm text-destructive">
                            {t('routes.platform.detail.wipe.failed')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {activeId === 'jobs' && (
              <section>
                <h2 className="text-sm font-semibold">
                  {t('routes.platform.detail.jobs.heading')}
                </h2>
                <div className="mt-2">
                  {applyJobId !== null ? (
                    <JobProgressIndicator jobExecutionId={applyJobId} />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('routes.platform.detail.jobs.none')}
                    </p>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <ApplyTemplateDialog
        open={applyOpen}
        onOpenChange={(next) => {
          setApplyOpen(next);
          if (!next) applyTemplate.reset();
        }}
        templates={templates}
        onSubmit={handleApply}
        pending={applyTemplate.isPending}
        submitError={applyTemplate.error}
      />

      {showDangerZone && (
        <WipeTenantConfirmDialog
          tenant={wipeOpen ? tenant : null}
          onCancel={() => {
            setWipeOpen(false);
            wipeTenant.reset();
          }}
          onConfirm={handleWipe}
          pending={wipeTenant.isPending}
          submitError={wipeTenant.error}
        />
      )}
    </div>
  );
}

/** Full-bleed header frame reused by the pending/error states. */
function DetailHeaderFrame({ children }: { readonly children: ReactNode }): ReactElement {
  const { t } = useTranslation();
  return (
    <header className="border-b border-border bg-card px-8 py-3">
      <Link
        to="/platform/tenants"
        className="mb-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        {t('routes.platform.detail.back')}
      </Link>
      {children}
    </header>
  );
}

interface TenantOverviewSectionProps {
  readonly schemaName: string;
  readonly uuid: string;
  readonly name: string;
  readonly stateLabel: string;
  readonly hasOverrides: boolean;
}

/** At-a-glance tenant facts. The default landing section. */
function TenantOverviewSection({
  schemaName,
  uuid,
  name,
  stateLabel,
  hasOverrides,
}: TenantOverviewSectionProps): ReactElement {
  const { t } = useTranslation();
  const rows: readonly { readonly label: string; readonly value: ReactNode }[] = [
    { label: t('routes.platform.detail.overview.name'), value: <span className="font-medium">{name}</span> },
    { label: t('routes.platform.detail.schemaLabel'), value: <code className="font-mono text-foreground">{schemaName}</code> },
    { label: t('routes.platform.detail.uuidLabel'), value: <code className="font-mono">{uuid}</code> },
    { label: t('routes.platform.detail.overview.state'), value: stateLabel },
    {
      label: t('routes.platform.detail.overview.overrides'),
      value: hasOverrides
        ? t('routes.platform.detail.overview.overridesYes')
        : t('routes.platform.detail.overview.overridesNo'),
    },
  ];

  return (
    <section aria-label={t('routes.platform.detail.sidebar.overview')}>
      <h2 className="text-sm font-semibold">{t('routes.platform.detail.overview.heading')}</h2>
      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[160px_1fr]">
        {rows.map((row) => (
          <div key={row.label} className="contents">
            <dt className="text-[12px] uppercase tracking-wide text-muted-foreground/80">{row.label}</dt>
            <dd className="text-sm text-foreground">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
